
// src/app/config/provide-config-slices.ts
import { Provider } from '@angular/core';
import { ConfigService } from './config.service';
import {
    PRODUCT_IDENTITY_TOKEN,
    APP_META_TOKEN,
    ENVIRONMENT_META_TOKEN,
    DIAGNOSTICS_META_TOKEN,
    DEBUG_INFO_TOKEN,
    MEMBERSHIP_CONFIG_TOKEN,
    AUTH_CONFIG_TOKEN,
    FEATURES_TOKEN,
    PRODUCT_URIS_TOKEN,
    USER_MANAGER_CONFIG_TOKEN,
    MESSAGE_OF_THE_DAY_CONFIG_TOKEN,
    GEOGRAPHICAL_SUMMARY_CONFIG_TOKEN,
    EGO_TOKEN
} from './config.tokens';

// Recursively freeze plain-object config slices so token consumers cannot mutate runtime config.
// Runtime config here is JSON-backed, so this object-only traversal is sufficient.
function deepFreeze<T>(obj: T): T {
    if (obj && typeof obj === 'object') {
        Object.freeze(obj as any);
        for (const key of Object.keys(obj as any)) {
            const val = (obj as any)[key];
            if (val && typeof val === 'object' && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}

/**
 * Registers DI tokens that expose runtime config slices from ConfigService.
 *
 * These factories run after APP_INITIALIZER has loaded runtime config,
 * so all ConfigService getters are expected to be ready here.
 */
export function provideConfigSlices(): Provider[] {
    return [
        // Full-object slices
        { provide: PRODUCT_IDENTITY_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.productIdentity()) },

        { provide: APP_META_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.appMeta()) },
        { provide: ENVIRONMENT_META_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.environment()) },
        { provide: DIAGNOSTICS_META_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.diagnostics()) },

        // Convenience primitive derived from diagnostics
        { provide: DEBUG_INFO_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => cfg.debugInfoEnabled() },

        { provide: MEMBERSHIP_CONFIG_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.membership()) },
        { provide: AUTH_CONFIG_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.auth()) },
        { provide: FEATURES_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.features()) },
        { provide: PRODUCT_URIS_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.productUris()) },

        { provide: USER_MANAGER_CONFIG_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.userManager()) },
        { provide: MESSAGE_OF_THE_DAY_CONFIG_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.messageOfTheDay()) },
        { provide: GEOGRAPHICAL_SUMMARY_CONFIG_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.geographicalSummary()) },
        { provide: EGO_TOKEN, deps: [ConfigService], useFactory: (cfg: ConfigService) => deepFreeze(cfg.ego()) },

    ];
}
